import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Github, Linkedin, Twitter, Download, ExternalLink, Code2, Palette, Terminal } from 'lucide-react';
import { SiGithub, SiLinkedin, SiX } from 'react-icons/si';
import { Experience, Skill, Project, SOCIAL_LINKS, scrollToSection } from '@/lib/index';
import { ExperienceCard } from '@/components/ExperienceCard';
import { SkillCard } from '@/components/SkillCard';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
const EXPERIENCES: Experience[] = [{
  id: '1',
  company: 'TechVision Studio',
  role: 'Senior Full Stack Developer',
  duration: '2023 - Present',
  location: 'Remote',
  achievements: ['Led the migration of a legacy monolithic architecture to a modern microservices-based React/Node.js stack.', 'Developed a custom UI library using Tailwind CSS and Framer Motion, improving development speed by 40%.', 'Mentored a team of 5 junior developers and implemented CI/CD pipelines using GitHub Actions.']
}, {
  id: '2',
  company: 'Creative Digital Agency',
  role: 'Lead Frontend Engineer',
  duration: '2021 - 2023',
  location: 'Paris, France',
  achievements: ['Designed and built immersive 3D web experiences using Three.js and React Three Fiber.', 'Optimized core web vitals for high-traffic e-commerce platforms, resulting in a 25% increase in conversion rate.', 'Collaborated closely with designers to ensure pixel-perfect implementation of complex interactive prototypes.']
}, {
  id: '3',
  company: 'InnovateX Startup',
  role: 'Software Developer',
  duration: '2019 - 2021',
  location: 'Algiers, Algeria',
  achievements: ['Developed cross-platform mobile applications using React Native and Firebase.', 'Implemented real-time data synchronization features for a collaborative project management tool.', 'Contributed to open-source libraries and maintained internal documentation.']
}];
const SKILLS: Skill[] = [{
  id: '1',
  name: 'React / Next.js',
  category: 'Frontend',
  proficiency: 95,
  iconName: 'Code2'
}, {
  id: '2',
  name: 'TypeScript',
  category: 'Frontend',
  proficiency: 90,
  iconName: 'Terminal'
}, {
  id: '3',
  name: 'Tailwind CSS',
  category: 'Design',
  proficiency: 98,
  iconName: 'Palette'
}, {
  id: '4',
  name: 'Node.js / Express',
  category: 'Backend',
  proficiency: 85,
  iconName: 'Terminal'
}, {
  id: '5',
  name: 'PostgreSQL',
  category: 'Backend',
  proficiency: 80,
  iconName: 'Terminal'
}, {
  id: '6',
  name: 'Framer Motion',
  category: 'Design',
  proficiency: 92,
  iconName: 'Palette'
}, {
  id: '7',
  name: 'Git / Docker',
  category: 'Tools',
  proficiency: 88,
  iconName: 'Code2'
}, {
  id: '8',
  name: 'System Design',
  category: 'Tools',
  proficiency: 85,
  iconName: 'Code2'
}];
const PROJECTS: Project[] = [{
  id: '1',
  title: 'Ethereal Dashboard',
  description: 'A futuristic analytics platform with glassmorphic UI and real-time data visualization using Recharts and WebSockets.',
  imageUrl: IMAGES.WORKSPACE_3,
  tags: ['Next.js', 'TypeScript', 'Tailwind', 'Recharts'],
  liveUrl: '#',
  githubUrl: '#',
  isFeatured: true
}, {
  id: '2',
  title: 'Nexus Commerce',
  description: 'A headless e-commerce solution with sub-second page loads and custom checkout experience.',
  imageUrl: IMAGES.OFFICE_BG_1,
  tags: ['React', 'Node.js', 'Stripe', 'Redis'],
  liveUrl: '#',
  githubUrl: '#',
  isFeatured: true
}];
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }
};
const Home: React.FC = () => {
  return <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.OFFICE_BG_1} alt="Background" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background to-background" />
        </div>

        <div className="container mx-auto px-4 z-10 text-center">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }}>
            <Badge variant="outline" className="mb-6 px-4 py-1 text-primary border-primary/20 backdrop-blur-sm">
              Available for 2026 Collaborations
            </Badge>
            <h1 className="text-6xl md:text-8xl font-bold font-sans tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/50">
              Crafting Digital <br /> 
              <span className="text-primary">Experiences.</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">I am Youcef, a Multilingual Operations Specialist - Senior Full Stack Developer dedicated to building high-performance web applications with a focus on creative engineering.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="rounded-full px-8 h-14 text-lg" onClick={() => scrollToSection('#projects')}>
                View My Work <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-8 h-14 text-lg" onClick={() => scrollToSection('#contact')}>
                Get In Touch
              </Button>
            </div>
          </motion.div>
        </div>

        <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2" animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }}>
          <div className="w-6 h-10 border-2 border-muted-foreground/30 rounded-full flex justify-center p-1">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-card/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-8">The Philosophy of <span className="text-primary">Precision.</span></h2>
              <div className="space-y-6 text-lg text-muted-foreground">
                <p>With over 5 years of professional experience, I have developed a deep appreciation for the intersection of logic and aesthetics. I don't just write code; I architect solutions that are as elegant as they are efficient.</p>
                <p>
                  My journey started with a passion for creative problem solving, leading me to master the full modern web stack. Today, I specialize in React ecosystems, cloud-native architectures, and high-fidelity interactive interfaces.
                </p>
                <div className="pt-6">
                  <Button variant="outline" className="rounded-full">
                    <Download className="mr-2 w-4 h-4" /> Download Resume
                  </Button>
                </div>
              </div>
            </motion.div>
            <motion.div {...fadeInUp} className="relative aspect-square rounded-3xl overflow-hidden group">
              <img src={IMAGES.PATTERN_7} alt="Decorative Abstract" className="w-full h-full object-cover grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700" />
              <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />
              <div className="absolute bottom-8 left-8 right-8 p-8 bg-background/80 backdrop-blur-xl border border-border/50 rounded-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-primary font-mono uppercase tracking-widest">Based In</p>
                    <p className="text-xl font-semibold">Bangkok, Thailand</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-primary font-mono uppercase tracking-widest">Experience</p>
                    <p className="text-xl font-semibold">5+ Years</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Career <span className="text-primary">Timeline.</span></h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A history of professional growth and technical leadership in diverse environments.
            </p>
          </motion.div>
          <div className="max-w-4xl mx-auto grid gap-8">
            {EXPERIENCES.map((exp, index) => <motion.div key={exp.id} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }}>
                <ExperienceCard experience={exp} />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-24 bg-secondary/50">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Technical <span className="text-primary">Arsenal.</span></h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Continuous learning and mastery of modern technologies to build resilient products.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SKILLS.map((skill, index) => <motion.div key={skill.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05,
            duration: 0.4
          }}>
                <SkillCard skill={skill} />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Selected <span className="text-primary">Works.</span></h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A collection of projects that demonstrate my commitment to performance and design.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {PROJECTS.map((project, index) => <motion.div key={project.id} {...fadeInUp} transition={{
            delay: index * 0.2
          }} className="group relative overflow-hidden rounded-3xl bg-card border border-border/50">
                <div className="aspect-video overflow-hidden">
                  <img src={project.imageUrl} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-80 group-hover:opacity-100" />
                </div>
                <div className="p-8">
                  <div className="flex gap-2 mb-4">
                    {project.tags.map(tag => <Badge key={tag} variant="secondary" className="bg-primary/5 text-primary-foreground/70 border-none">{tag}</Badge>)}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{project.title}</h3>
                  <p className="text-muted-foreground mb-6 line-clamp-2">{project.description}</p>
                  <div className="flex items-center gap-4">
                    <a href={project.liveUrl} className="inline-flex items-center text-primary font-semibold hover:underline">
                      Live Demo <ExternalLink className="ml-1 w-4 h-4" />
                    </a>
                    <a href={project.githubUrl} className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
                      Source <Github className="ml-1 w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <motion.div {...fadeInUp}>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">Let's Build <br /><span className="text-primary">Together.</span></h2>
                <p className="text-muted-foreground text-lg mb-10">
                  Whether you have a specific project in mind or just want to say hi, my inbox is always open. Let's create something extraordinary.
                </p>
                <div className="space-y-6">
                  <a href={SOCIAL_LINKS.email} className="flex items-center gap-4 p-4 rounded-2xl border border-border/50 hover:bg-primary/5 transition-colors group">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email Me</p>
                      <p className="text-lg font-medium">youcefbenai85@gmail.com</p>
                    </div>
                  </a>
                  <div className="flex gap-4">
                    <a href={SOCIAL_LINKS.github} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-xl border border-border/50 flex items-center justify-center hover:bg-primary/5 hover:text-primary transition-all">
                      <SiGithub className="w-6 h-6" />
                    </a>
                    <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-xl border border-border/50 flex items-center justify-center hover:bg-primary/5 hover:text-primary transition-all">
                      <SiLinkedin className="w-6 h-6" />
                    </a>
                    <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-xl border border-border/50 flex items-center justify-center hover:bg-primary/5 hover:text-primary transition-all">
                      <SiX className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fadeInUp} transition={{
              delay: 0.2
            }}>
                <Card className="p-8 border-border/50 shadow-xl">
                  <form className="space-y-6" onSubmit={e => e.preventDefault()}>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Full Name</label>
                      <input type="text" placeholder="John Doe" className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Email Address</label>
                      <input type="email" placeholder="john@example.com" className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Message</label>
                      <textarea rows={4} placeholder="How can I help you?" className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all resize-none" />
                    </div>
                    <Button className="w-full h-12 rounded-xl text-lg font-semibold shadow-lg shadow-primary/20">
                      Send Message
                    </Button>
                  </form>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-border/50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground font-mono text-sm">
            © 2026 Youcef Portfolio. Built with React & TypeScript. All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
};
export default Home;